/**
 * O cadastro de produtos: de um lado os códigos que a Condusvale usa no ERP,
 * de outro o nome que cada fornecedor dá ao mesmo cabo.
 */

export type ResumoDeItens = {
  total: number;
  ativos: number;
  marcas: string[];
  cores: string[];
};

export type ResumoDeVinculos = {
  total: number;
  confirmados: number;
  fornecedores: string[];
};

export type ProdutoDaLista = {
  id: number;
  descricao: string;
  familia: string;
  secao_mm2: number | null;
  vias: number;
  itens_erp: ResumoDeItens;
  vinculos: ResumoDeVinculos;
};

export type ListaDeProdutos = {
  data: ProdutoDaLista[];
  meta: { total: number; marcas: string[]; sem_produto: number };
};

export type ItemErp = {
  id: number;
  codigo: string;
  descricao: string;
  marca: string | null;
  cor: string | null;
  sigla_cor: string | null;
  unidade: string;
  embalagem: string | null;
  ativo: boolean;
  visto_em: string | null;
  codigos_fornecedor: CodigoDoFornecedor[];
};

/**
 * O de-para da compra: o código que um fornecedor dá a este item, nesta cor.
 *
 * `codigo` é o que veio do orçamento e `codigo_cadastro` o que o Compumate
 * guarda. Quase sempre são o mesmo item; quando o do cadastro é o do orçamento
 * mais um sufixo — a cor da SIL, a embalagem da Cobrecom —, é ele que vai no
 * pedido, e é isso que `codigo_pedido` já traz resolvido.
 */
export type CodigoDoFornecedor = {
  id: number;
  fornecedor_id: number;
  apelido: string;
  codigo: string | null;
  codigo_cadastro: string | null;
  codigo_pedido: string | null;
  situacao: 'ausente' | 'confere' | 'completa' | 'so_cadastro' | 'diverge';
  descricao: string | null;
  origem: 'automatico' | 'manual' | 'cadastro';
  confirmado_em: string | null;
};

export type Vinculo = {
  id: number;
  descricao_fornecedor: string;
  codigo_fornecedor: string | null;
  embalagem: string | null;
  fator_para_metro: number;
  origem: 'automatico' | 'manual';
  confirmado_por: string | null;
  confirmado_em: string | null;
};

export type FornecedorComVinculos = {
  fornecedor: { id: number; apelido: string; logo_url: string | null };
  vinculos: Vinculo[];
};

export type DetalheDoProduto = {
  produto: {
    id: number;
    descricao: string;
    familia: string;
    secao_mm2: number | null;
    vias: number;
    unidade_base: string;
  };
  itens_erp: ItemErp[];
  fornecedores: FornecedorComVinculos[];
};

/**
 * O que o cadastro da Condusvale tem a dizer sobre o de-para da compra.
 *
 * Quem manda continua sendo o orçamento do fornecedor; a coluna "Codigo Produto
 * Fornec" da Base Geral entra para conferir, completar a cor que a SIL não
 * codifica no PDF e apontar o que discorda.
 */
export type ConferenciaDoDePara = {
  com_codigo: number;
  sem_codigo: number;
  conferem: number;
  completam: number;
  novos: number;
  sem_fornecedor: number;
  divergem: { codigo: string; descricao: string; cadastro: string; orcamento: string | null }[];
  codigo_da_casa: { codigo: string; descricao: string; cadastro: string; orcamento: string | null }[];
};

export type PreviaDosItens = {
  arquivo: string;
  de_para: ConferenciaDoDePara;
  linhas_lidas: number;
  novos: number;
  atualizados: number;
  inativos: number;
  sem_produto: number;
  produtos_novos: number;
  por_marca: Record<string, number>;
  amostra: {
    codigo: string;
    descricao: string;
    produto: string | null;
    marca: string | null;
    cor: string | null;
    unidade: string;
    embalagem: string | null;
    custo_unitario: number | null;
    ativo: boolean;
  }[];
};

/** Como a família é escrita na tela; o banco guarda o slug. */
export const NOME_FAMILIA: Record<string, string> = {
  flex_750: 'Flexível 750 V',
  flex_hepr: 'Flexível HEPR 1 kV',
  rigido_750: 'Rígido 750 V',
  rigido_hepr: 'Rígido HEPR 1 kV',
  solar: 'Solar',
  pp: 'PP',
  paralelo: 'Paralelo',
  cobre_nu: 'Cobre nu',
  multilan: 'Rede',
  outro: 'Outro',
};
